import { createStyleUtils } from '@wix/thunderbolt-commons'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import {
	ComponentsStylesOverridesSymbol,
	EditorFeatureConfigSymbol,
	IComponentsStylesOverrides,
	IPageWillMountHandler,
	PageFeatureConfigSymbol,
} from '@wix/thunderbolt-symbols'
import { IModelUpdatesHandlersImplementor } from 'ds-feature-model-updates-invoker'
import { name } from './symbols'
import { VisibilityEditorConfig, VisibilityHandlers, VisibilityEditorPageConfig, CompVisibilityData } from './types'

const COLLAPSED_EDIT_STYLE = {
	position: 'absolute',
	top: '0',
	content: `''`,
	height: '100%',
	width: '100%',
	background: `repeating-linear-gradient(
									-45deg,
									transparent,
									transparent 40%,
									#2b5672c7 40%,
									#2b5672c7 45%,
									#cbcddee8 45%,
									#cbcddee8 50%,
									transparent 50%)`,
	'background-size': '10px 10px',
	'background-repeat': 'repeat',
	'pointer-events': 'none',
}

const RESET_COLLAPSED = {
	position: null,
	top: null,
	bottom: null,
	left: null,
	right: null,
	content: null,
	background: null,
}

const classicStyleUtils = createStyleUtils({ isResponsive: false })
const responsiveStyleUtils = createStyleUtils({ isResponsive: true })

const getVisibilityStyleOverrides: (
	data: CompVisibilityData,
	isEditorMode: boolean
) => { [id: string]: Record<string, string | null> } = (
	{
		compId,
		isResponsive,
		isHiddenCompProp,
		isCollapsedCompProp,
		showHiddenComponents,
		ignoreHidden,
		ignoreCollapsed,
	},
	isEditorMode
) => {
	const styleUtils = isResponsive ? responsiveStyleUtils : classicStyleUtils
	const styles: Record<string, string | null> = {}
	const styleOverrides = { [compId]: styles }

	// There are four cases we should address:
	// 1. The component is not marked as collapsed - should be displayed regular
	// 2. The component is marked as collapsed, we are in the editor and the
	// component is not selected - should be displayed with an overlay
	// 3. The component is marked as collapsed, we are in the editor and the
	// component is selected - should be displayed regular
	// 4. The component is marked as collapsed, we are in preview - should be displayed
	// as none at the beginning and when expanded should be displayed without overlay
	if (isCollapsedCompProp && !ignoreCollapsed) {
		if (isEditorMode) {
			Object.assign(styles, styleUtils.getExpandedStyles())
			styleOverrides[`${compId}::after`] = COLLAPSED_EDIT_STYLE
		} else {
			Object.assign(styles, styleUtils.getCollapsedStyles())
			styleOverrides[`${compId}::after`] = RESET_COLLAPSED
		}
	} else {
		Object.assign(styles, styleUtils.getExpandedStyles())
		styleOverrides[`${compId}::after`] = RESET_COLLAPSED
	}

	// There are four cases we should address:
	// 1. The component is not marked as hidden - should be displayed regular
	// 2. The component is marked as hidden, we are in the editor and the
	// component is not selected - should be displayed with opacity
	// 3. The component is marked as hidden, we are in the editor and the
	// component is selected - should be displayed regular
	// 4. The component is marked as hidden, we are in preview - should be displayed
	// as none at the beginning and when expanded should be displayed without opacity
	if (isHiddenCompProp && !ignoreHidden) {
		if (showHiddenComponents) {
			Object.assign(styles, styleUtils.getShownStyles(), { opacity: 0.5 })
		} else {
			Object.assign(styles, styleUtils.getHiddenStyles(), { opacity: null })
		}
	} else {
		Object.assign(styles, { opacity: null })

		if (!styles.visibility) {
			Object.assign(styles, styleUtils.getShownStyles())
		}
	}

	return styleOverrides
}

export const VisibilityUpdateHandlers = withDependencies(
	[named(EditorFeatureConfigSymbol, name), ComponentsStylesOverridesSymbol],
	(
		config: VisibilityEditorConfig,
		componentsStylesOverrides: IComponentsStylesOverrides
	): IModelUpdatesHandlersImplementor<VisibilityHandlers> => {
		return {
			featureName: name,
			handlers: {
				updateStyles: ({
					compId,
					isResponsive,
					isHiddenCompProp,
					isCollapsedCompProp,
					showHiddenComponents,
					ignoreHidden,
					ignoreCollapsed,
				}) => {
					componentsStylesOverrides.update(
						getVisibilityStyleOverrides(
							{
								compId,
								isResponsive,
								isHiddenCompProp,
								isCollapsedCompProp,
								showHiddenComponents,
								ignoreHidden,
								ignoreCollapsed,
							},
							config.isEditorMode
						)
					)
				},
			},
		}
	}
)

export const PageWillMountVisibilityHandlers = withDependencies(
	[named(PageFeatureConfigSymbol, name), ComponentsStylesOverridesSymbol],
	(
		config: VisibilityEditorPageConfig,
		componentsStylesOverrides: IComponentsStylesOverrides
	): IPageWillMountHandler => {
		return {
			name: 'visibilityUpdateHandlers',
			pageWillMount: () => {
				Object.values(config.hiddenOrCollapsedComps).forEach((comp) => {
					const visibilityStyleOverrides = getVisibilityStyleOverrides(comp, config.isEditorMode)
					componentsStylesOverrides.update(visibilityStyleOverrides)
				})
			},
		}
	}
)
